import { useEffect, useRef, useState, FocusEvent } from "react"
import { css, cx } from "@emotion/css"
import { useI18n } from "providers/I18n"

import type { DimensionOption, DimensionOptionType } from "models/ratecard"
//
import { ReactComponent as MoreIcon } from "assets/more.svg"

import IconButton from "@material-ui/core/IconButton"
import ListItemText from "@material-ui/core/ListItemText"
import Menu from "@material-ui/core/Menu"
import MenuItem from "@material-ui/core/MenuItem"

import * as DG from "ui/dialogs"
import Button from "ui/buttons/Button"

import { StyledTable } from "./components/StyledTable"
import Input from "ui/inputs/Input"
import GhostSelect from "ui/inputs/GhostSelect"
import GhostTextInput from "ui/inputs/GhostTextInput"
import { formatFloatingPointNumber, parseNumber } from "utils/number-format"
import { useRifm } from "rifm"
import { Box } from "@material-ui/core"
import { useToggle } from "hooks/useToggle"
import { v1 as getUuid } from "uuid"
import React from "react"

type Col<T> = {
  id: string
  cell: React.ComponentType<CellProps<T>>
  align?: "center" | "left" | "right"
  width?: React.CSSProperties["width"]
}

type CellProps<T> = {
  row: T
  remove: (id: string) => void
  update: (row: T & { id?: string }) => void
  updateEveryRow: (f: (row: T, index: number) => T) => void
}

const cols: Col<EnrichedDimOptions>[] = [
  { id: "duration", cell: DurationCell, align: "left", width: 125 },
  { id: "type", cell: TypeCell, align: "left" },
  { id: "actions", cell: ActionCell, align: "center", width: 80 },
]

const newRowCols: Col<EnrichedDimOptions>[] = [
  { id: "duration", cell: DurationCell, align: "left", width: 125 },
  { id: "type", cell: TypeCell, align: "left" },
  { id: "actions", cell: ActionCell, align: "center", width: 80 },
]

const initialNewRow = {
  type: "MANUAL" as DimensionOptionType,
  value: "",
  isDefault: false,
}

type DurationConfigModalProps = {
  open: boolean
  onClose: () => void
  //
  onDone: (options: DimensionOption[]) => void
  getInitial: () => EnrichedDimOptions[]
}

type EnrichedDimOptions = DimensionOption & { id?: string }

export function DurationConfigModal(props: DurationConfigModalProps) {
  const { open, onClose, getInitial, onDone } = props
  const { t } = useI18n()
  const gi = useRef(getInitial)
  gi.current = getInitial
  const getInitialNewRow = (): EnrichedDimOptions => initialNewRow

  const [rows, setRows] = useState<EnrichedDimOptions[]>([])
  const [newRow, setNewRow] = useState<EnrichedDimOptions>(getInitialNewRow)
  const {
    open: openNewRow,
    onOpen: onNewRowOpen,
    onClose: onNewRowClose,
  } = useToggle()

  useEffect(() => {
    setRows(gi.current)
    return () => setRows(() => [])
  }, [open])

  const removeRow = (id: string) => {
    if (!!id) {
      setRows((rows) => remove(id, rows))
    } else {
      setNewRow(getInitialNewRow)
      onNewRowClose()
    }
  }

  const updateRow = (row: EnrichedDimOptions) => {
    const updatedRows = rows.map((r) => (r.id === row?.id ? row : r))
    setRows(() => updatedRows)
  }

  const updateEveryRow = (
    updater: (r: EnrichedDimOptions, index: number) => EnrichedDimOptions,
  ) => setRows((rows) => rows.map(updater))

  const updateNewRow = (row: EnrichedDimOptions) => {
    setNewRow(() => row)
    setRows([{ ...row, id: getUuid() }, ...rows])
    onNewRowClose()
  }

  const addNewRow = () => {
    setNewRow(getInitialNewRow)
    onNewRowOpen()
  }

  const handleSubmit = () => {
    if (rows.length) {
      const rowsStrpd = rows.map(({ id, ...rest }) => rest)
      onDone(rowsStrpd)
      setRows([])
      onClose()
    }
  }

  const handleClose = () => {
    setRows([])
    onClose()
  }
  // options.should.have.exactly.one.default
  const hasOneDefault =
    rows.length >= 0 && rows.filter((x) => Boolean(x.isDefault)).length === 1
  const valid = hasOneDefault && areRowsValid(rows)
  const id = "duration-configuration-modal"

  return (
    <DG.Dialog open={open} onClose={handleClose} id={id}>
      <DG.DialogHeader>
        <DG.DialogTitle id={id}>
          {t("rate.card.dimensions.duration")}
        </DG.DialogTitle>
        <DG.DialogClose onClose={handleClose} />
      </DG.DialogHeader>

      <DG.DialogContent center paddingTop>
        <div>
          <Box
            display="flex"
            justifyContent="flex-end"
            className={css`
              padding-bottom: var(--spacing-m);
            `}
          >
            <Button
              color="primary"
              variant="contained"
              onClick={addNewRow}
              disabled={openNewRow && !newRow?.value}
            >
              {t("rate.card.dimensions.duration.new")}
            </Button>
          </Box>
          <StyledTable>
            <thead>
              <tr role="row">
                {cols.map((col) => {
                  return (
                    <th
                      key={col.id}
                      role="columnheader"
                      className={cx(col.align)}
                      style={{ width: col.width }}
                    >
                      <span>{col.id}</span>
                    </th>
                  )
                })}
              </tr>
            </thead>
            <tbody>
              {/* <tfoot> */}
              <tr role="row">
                {openNewRow
                  ? newRowCols.map((col) => {
                      const { id, cell: Cell, align = "left", width } = col
                      return (
                        <td
                          role="cell"
                          key={`${rows.length}_${id}`}
                          className={cx(align)}
                          style={{ width }}
                        >
                          <Cell
                            row={newRow}
                            update={updateNewRow}
                            remove={removeRow}
                            updateEveryRow={updateEveryRow}
                          />
                        </td>
                      )
                    })
                  : null}
              </tr>
              {rows?.map((row, rowIndex) => {
                return (
                  <tr key={row?.id} role="row">
                    {cols.map((col) => {
                      const { id, cell: Cell, align = "left", width } = col
                      return (
                        <td
                          role="cell"
                          key={`${row?.id}_${id}`}
                          className={cx(align)}
                          style={{ width }}
                        >
                          <Cell
                            row={row}
                            remove={removeRow}
                            update={updateRow}
                            updateEveryRow={updateEveryRow}
                          />
                        </td>
                      )
                    })}
                  </tr>
                )
              })}
            </tbody>
          </StyledTable>
        </div>
      </DG.DialogContent>
      <DG.DialogFooter>
        <DG.DialogActions>
          <Button variant="outlined" color="primary" onClick={handleClose}>
            {t("actions.cancel")}
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSubmit}
            disabled={!valid}
          >
            {t("actions.done")}
          </Button>
        </DG.DialogActions>
      </DG.DialogFooter>
    </DG.Dialog>
  )
}

function remove<T extends Record<string, any>>(id: string, list: T[]) {
  return list?.filter((l) => l?.id !== id)
}

function DurationCell(props: CellProps<EnrichedDimOptions>) {
  const { row, update } = props

  const handleBlur = (evt: FocusEvent<HTMLInputElement>) => {
    const { value } = evt.target
    const v = value || ""
    if (v === "") update({ ...row, value: v })
    const digits = /^\d+$/.test(v)
    const int = parseInt(v)
    if (!digits || isNaN(int)) return

    update({ ...row, value: v })
  }

  return (
    <div
      className={css`
        display: flex;
      `}
    >
      <Input
        placeholder="Add duration"
        defaultValue={row.value}
        onBlur={handleBlur}
        pattern="[0-9]*"
        type="number"
        inputMode="numeric"
      />
      {row.isDefault ? (
        <span
          className={css`
            font-size: 12px;
            font-weight: 300;
            color: var(--dark-grey);
            margin: 0 var(--spacing-xs);
          `}
        >
          Default
        </span>
      ) : null}
    </div>
  )
}

const typeOptions = [
  { value: "MANUAL", label: "Manual" },
  { value: "PERCENTAGE", label: "Percentage" },
]

function TypeCell(props: CellProps<EnrichedDimOptions>) {
  const { row, update } = props
  const type = row.type
  const pc = row.percentage

  const handleType = (value?: string) => {
    const type = (value ?? "MANUAL") as DimensionOptionType
    let percentage = row.percentage
    if (type === "MANUAL" && row.percentage !== undefined) {
      percentage = undefined
    }
    update({ ...row, type: type, percentage })
  }

  const setPercentage = (percentage: number | undefined) =>
    update({ ...row, percentage })

  const [local, setLocal] = React.useState<string>(pc?.toString() ?? "")

  const handlePcBlur = () => {
    if (local === "") setPercentage(undefined)
    const num = Number(local)
    setPercentage(num)
  }

  const rifm = useRifm({
    value: local,
    accept: /[\d.]/g,
    onChange: (v) => {
      setLocal(parseNumber(v))
    },
    format: formatFloatingPointNumber(2),
  })

  return (
    <div
      className={css`
        display: flex;
        gap: 4px;
      `}
    >
      <GhostSelect
        options={typeOptions}
        value={type}
        onSelectedOptionChange={({ value }) => handleType(value)}
      />
      {type === "PERCENTAGE" ? (
        <GhostTextInput
          value={rifm.value}
          onChange={rifm.onChange}
          onBlur={handlePcBlur}
          placeholder={"Add %"}
          type="text"
          inputMode="decimal"
        />
      ) : null}
    </div>
  )
}

function ActionCell(props: CellProps<EnrichedDimOptions>) {
  const { row, remove, updateEveryRow } = props

  //
  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null)
  const open = (evt: React.MouseEvent<HTMLButtonElement>) =>
    setAnchorEl(evt.currentTarget)
  const close = () => setAnchorEl(null)
  //

  const handleDelete = (evt?: React.MouseEvent<HTMLLIElement>) => {
    const { id } = evt?.currentTarget ?? {}
    remove(id || "")
    close()
  }

  const handleSetDefault = (evt?: React.MouseEvent<HTMLLIElement>) => {
    const { id } = evt?.currentTarget ?? {}
    updateEveryRow((row) => ({ ...row, isDefault: row?.id === id }))
    close()
  }

  const canBeDefault = row.type === "MANUAL" && !row.isDefault && !!row?.value

  return (
    <div>
      <IconButton onClick={open} size="small">
        <MoreIcon />
      </IconButton>

      <Menu
        id="actions-list"
        anchorEl={anchorEl}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        keepMounted={false}
        open={Boolean(anchorEl)}
        onClose={close}
      >
        {canBeDefault ? (
          <MenuItem onClick={handleSetDefault} id={row?.id}>
            <ListItemText primary={"Set as default"} />
          </MenuItem>
        ) : null}
        <MenuItem onClick={handleDelete} id={row?.id}>
          <ListItemText
            primary={<span style={{ color: "var(--red)" }}>Delete</span>}
          />
        </MenuItem>
      </Menu>
    </div>
  )
}

const areRowsValid = (rows: EnrichedDimOptions[]): boolean => {
  const valuesOk = rows?.every((r) => r?.value >= 0 && r?.value !== "")
  const pcOk = rows?.every((r) => {
    let isPcOk: boolean = true
    if (r?.type === "PERCENTAGE") {
      isPcOk = !!r?.percentage
    }
    return isPcOk
  })
  return valuesOk && pcOk
}
